import { Box, DialogContent, useMediaQuery } from '@mui/material'
import PropTypes from 'prop-types'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useTheme } from '@mui/material/styles'
import FlipCameraIosOutlinedIcon from '@mui/icons-material/FlipCameraIosOutlined'

import cameraIcon from '@hapana/shared/web/styles/assets/svgs/camera.svg'
import {
  BUTTON_SIZE,
  BUTTON_THEME,
  BUTTON_VARIANTS,
} from '../../../components/shared/button/button.constants'
import DialogLayout from '../../../components/shared/dialog/dialog-layout/dialog-layout.component'
import {
  CLOSE_TYPES,
  SIZES,
} from '../../../components/shared/dialog/dialog-layout/dialog-layout.constants'
import { useDialog } from '../../../components/shared/dialog/dialog.hooks'
import { useCounterInterval } from '../../../hooks/interval.hooks'
import { useUserMedia } from '../../../hooks/useUserMedia.hooks'
import CameraFrame from '../../../components/shared/camera-frame/camera-frame.component'

import ModalAllowCamera from './modal-allow-camera.component'
import {
  END_COUNTER,
  EXCEPTIONS_MODALS,
  INITIAL_COUNTER,
  INTERVAL_DELAY,
  MODAL_ALLOW_CAMERA_TYPES,
  USER_MEDIA_CONSTRAINTS,
  CONVERT_QUALITY,
  FRAME_SIZE,
  FRAME_SIZE_MOBILE,
  FACING_MODES,
} from './modal-upload-photo.constants'
import { cameraFramingStyles } from './modal-upload-photo.styles'
import ModalCropClientPhoto from './modal-crop-photo.component'

const ModalCameraFraming = ({ isCameraEnabled, mutation, isBannerImage }) => {
  const { pushDialog, filterDialogStack, dialogState } = useDialog()
  const { t } = useTranslation('lib')
  const { hasFinished, hasStarted, start, counter, isCounting } = useCounterInterval(
    INTERVAL_DELAY,
    INITIAL_COUNTER,
    END_COUNTER
  )
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const frameSize = isMobile ? FRAME_SIZE_MOBILE : FRAME_SIZE

  const [facingMode, setFacingMode] = useState(FACING_MODES.ENVIRONMENT)

  const onError = useCallback(
    (error) => {
      pushDialog(EXCEPTIONS_MODALS[error.name], { hasCam: false })
    },
    [pushDialog]
  )

  const { ref: mediaRef, isStreamEnabled } = useUserMedia({
    constraints: USER_MEDIA_CONSTRAINTS,
    facingMode,
    onError,
  })

  const isDisabled = !isStreamEnabled || hasStarted

  const capturePhotoCallback = useCallback(
    (photo) => {
      const timeoutId = setTimeout(async () => {
        // Convert the photo URL back to a blob, then to a File object
        try {
          const response = await fetch(photo)
          const blob = await response.blob()

          // Create a File object from the blob
          const file = new File([blob], `camera-photo-${Date.now()}.jpg`, {
            type: 'image/jpeg',
            lastModified: Date.now(),
          })

          pushDialog(ModalCropClientPhoto, {
            image: photo,
            originalFile: file,
            mutation,
            isBannerImage,
          })
        } catch (error) {
          console.error('Failed to create file from camera photo:', error)
          // Fallback - still open the dialog but with a dummy file
          const dummyFile = new File([''], `camera-photo-${Date.now()}.jpg`, {
            type: 'image/jpeg',
            lastModified: Date.now(),
          })

          pushDialog(ModalCropClientPhoto, {
            image: photo,
            originalFile: dummyFile,
            mutation,
            isBannerImage,
          })
        }
      }, 500)
      return () => clearInterval(timeoutId)
    },
    [pushDialog, mutation, isBannerImage]
  )

  const toggleFacingMode = () => {
    setFacingMode((prevMode) =>
      prevMode === FACING_MODES.USER ? FACING_MODES.ENVIRONMENT : FACING_MODES.USER
    )
  }

  const primaryActionButtonProps = {
    variant: BUTTON_VARIANTS.ICON,
    buttonIconTheme: BUTTON_THEME.PRIMARY,
    disabled: isDisabled,
    size: BUTTON_SIZE.MEDIUM,
    sx: [
      cameraFramingStyles.primaryAction,
      isCounting && cameraFramingStyles.primaryActionCounter,
      hasFinished && cameraFramingStyles.capturing,
    ],
    ...(isCounting ? { children: counter } : { icon: cameraIcon }),
    iconProps: {
      viewBox: '0 0 16 17',
      sx: cameraFramingStyles.primaryActionIcon,
    },
  }

  const secondaryActionButtonProps = {
    variant: BUTTON_VARIANTS.ICON,
    buttonIconTheme: BUTTON_THEME.SECONDARY,
    disabled: isDisabled,
    size: BUTTON_SIZE.MEDIUM,
    ...(isCounting ? { children: null } : { icon: FlipCameraIosOutlinedIcon }),
  }

  const dialogLayoutProps = {
    closeAction: () =>
      filterDialogStack(
        dialogState?.dialogFilterParams?.key,
        dialogState?.dialogFilterParams?.value
      ),
    primaryAction: {
      label: t('lib:profilePicture.startCapture'),
      action: () => start(),
      containerProps: {
        sx: cameraFramingStyles.primaryActionContainerMobile,
      },
      props: primaryActionButtonProps,
    },
    secondaryAction: {
      label: t('lib:profilePicture.switchCamera'),
      action: toggleFacingMode,
      containerProps: {
        sx: cameraFramingStyles.secondaryActionContainer,
      },
      props: secondaryActionButtonProps,
    },
    ...(isStreamEnabled
      ? { title: t('lib:profilePicture.cameraFraming') }
      : { headerStylesOverride: cameraFramingStyles.dialogHeader }),
  }

  return (
    <DialogLayout
      {...dialogLayoutProps}
      closeType={isStreamEnabled ? CLOSE_TYPES.TEXT : CLOSE_TYPES.ICON}
      size={SIZES.XS}
      hasMobileBottomSheet
    >
      {isStreamEnabled ? (
        <DialogContent sx={cameraFramingStyles.dialogContent}>
          <Box sx={cameraFramingStyles.container}>
            <CameraFrame
              mediaRef={mediaRef}
              hasFinished={hasFinished}
              hasStarted={hasStarted}
              capturePhotoCallback={capturePhotoCallback}
              convertQuality={CONVERT_QUALITY}
              frameSize={frameSize}
            />
          </Box>
        </DialogContent>
      ) : (
        <ModalAllowCamera
          allowType={
            isCameraEnabled ? MODAL_ALLOW_CAMERA_TYPES.STARTING : MODAL_ALLOW_CAMERA_TYPES.ALLOW
          }
        />
      )}
    </DialogLayout>
  )
}

ModalCameraFraming.propTypes = {
  isCameraEnabled: PropTypes.bool,
  isBannerImage: PropTypes.bool,
  clientId: PropTypes.string,
  mutation: PropTypes.shape({
    mutate: PropTypes.func.isRequired,
    isLoading: PropTypes.bool.isRequired,
    responseKey: PropTypes.string.isRequired,
    onUploadSuccess: PropTypes.func,
  }).isRequired,
}

ModalCameraFraming.defaultProps = {
  isCameraEnabled: false,
  isBannerImage: false,
  clientId: '',
}

export default ModalCameraFraming
